import * as React from "react";
import { QueryClient, UseQueryOptions } from "@tanstack/react-query";
import {
  getAppDataGetAppDataQueryKey,
  getAppDataGetAppDataQueryOptions,
} from "src/model/app-data";
import {
  getInsightsGetInsightsQueryKey,
  getInsightsGetInsightsQueryOptions,
} from "src/model/insights";
import { getAuth } from "firebase/auth";
import {
  getGetAppConfigQueryKey,
  getGetAppConfigQueryOptions,
  getAppConfig,
} from "src/model_v2/app-config";
import {
  getImportsGetUserImportSourcesQueryKey,
  getImportsGetUserImportSourcesQueryOptions,
} from "src/model/imports";
import { getGetSettingsQueryKey } from "src/model_v2/settings";
import { getAppConfigGetAppConfigQueryKey } from "@/model/app-config";
import { getSettingsReadSettingsQueryKey } from "@/model/settings";
import {
  getGetUserImportSourcesQueryKey,
  getGetUserImportSourcesQueryOptions,
} from "@/model_v2/imports";
import {
  getGetAppDataQueryKey,
  getGetAppDataQueryOptions,
  getAppData,
} from "@/model_v2/app-data";
import {
  getListImportOperationsQueryKey,
  getListAccountsQueryOptions,
  getListAccountsQueryKey,
} from "@/model_v2/default";
import type { ApiAppDataResponse } from "@/model_v2/client.schemas";
import type { AxiosResponse } from "axios";
import * as axios from "axios";

// Cache durations
const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// App data query params
export interface AppDataQueryParams {
  include_attributes?: boolean;
}

/**
 * Check if any tag columns are visible in the trades table.
 * Tag columns require attributes data from the API.
 */
export const hasVisibleTagColumns = (): boolean => {
  try {
    const stored = localStorage.getItem("tradesTableVisibility");
    if (!stored) return false;
    const visibility = JSON.parse(stored) as Record<string, boolean>;
    // Check if any visible column starts with "tag_"
    return Object.entries(visibility).some(
      ([key, isVisible]) => key.startsWith("tag_") && isVisible
    );
  } catch {
    return false;
  }
};

/**
 * Hook that returns whether tag columns need attributes and updates when localStorage changes.
 * This ensures the component re-renders when a user adds/removes tag columns.
 * When switching to needing attributes, it prefetches the data to avoid Suspense flash.
 * Also returns loading state so components can show spinners on tag columns.
 */
export const useNeedsAttributes = (): { needsAttributes: boolean; isLoadingAttributes: boolean } => {
  const [needsAttributes, setNeedsAttributes] = React.useState(hasVisibleTagColumns);
  const [isLoadingAttributes, setIsLoadingAttributes] = React.useState(false);

  React.useEffect(() => {
    // Check on mount
    setNeedsAttributes(hasVisibleTagColumns());

    // Handler that prefetches data if switching to needing attributes
    const handleVisibilityChange = async () => {
      const nowNeedsAttributes = hasVisibleTagColumns();

      // If switching from not needing to needing attributes, prefetch first
      if (nowNeedsAttributes) {
        setIsLoadingAttributes(true);
        const queryOptions = getGetAppDataWithParamsQueryOptions({ include_attributes: true });
        // Prefetch and wait for it to complete before updating state
        await queryClient.prefetchQuery(queryOptions);
        setIsLoadingAttributes(false);
      }

      setNeedsAttributes(nowNeedsAttributes);
    };

    // Listen for storage changes (works across tabs too)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "tradesTableVisibility") {
        handleVisibilityChange();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("tradesTableVisibilityChanged", handleVisibilityChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("tradesTableVisibilityChanged", handleVisibilityChange);
    };
  }, []);

  return { needsAttributes, isLoadingAttributes };
};

// Custom fetcher for app data with parameters
export const getAppDataWithParams = (
  params?: AppDataQueryParams,
): Promise<AxiosResponse<ApiAppDataResponse>> => {
  const queryParams = new URLSearchParams();
  if (params?.include_attributes) {
    queryParams.set("include_attributes", "true");
  }
  const queryString = queryParams.toString();
  const url = queryString ? `/v2/app_data?${queryString}` : `/v2/app_data`;
  return axios.default.get(url);
};

// Query key that includes the params for proper cache separation
export const getGetAppDataWithParamsQueryKey = (params?: AppDataQueryParams) => {
  // Use string representation of include_attributes for consistent query key typing
  const includeAttrs = params?.include_attributes ? "with_attributes" : "compact";
  return [`/v2/app_data`, includeAttrs] as const;
};

// Query options with params support
export const getGetAppDataWithParamsQueryOptions = <
  TData = Awaited<ReturnType<typeof getAppDataWithParams>>,
>(
  params?: AppDataQueryParams,
): UseQueryOptions<Awaited<ReturnType<typeof getAppDataWithParams>>, unknown, TData> & {
  queryKey: readonly [string, string];
} => {
  const queryKey = getGetAppDataWithParamsQueryKey(params);
  return {
    queryKey,
    queryFn: ({ signal }) =>
      getAppDataWithParams(params).then((res) => res),
  } as UseQueryOptions<Awaited<ReturnType<typeof getAppDataWithParams>>, unknown, TData> & {
    queryKey: readonly [string, string];
  };
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: FIFTEEN_MINUTES_MS, // 15 Minutes for most data
    },
  },
});

// Query keys for different data types
// Note: App data queries with different include_attributes values have different keys
export const APP_DATA_QUERY_KEYS = [
  getAppDataGetAppDataQueryKey(),
  getGetAppDataQueryKey(),
  getGetAppDataWithParamsQueryKey(), // without attributes
  getGetAppDataWithParamsQueryKey({ include_attributes: true }), // with attributes
  getInsightsGetInsightsQueryKey(),
] as const;

export const STATIC_CONFIG_QUERY_KEYS = [
  getGetAppConfigQueryKey(),
  getAppConfigGetAppConfigQueryKey(),
  getGetSettingsQueryKey(),
  getSettingsReadSettingsQueryKey(),
] as const;

export const IMPORT_SOURCES_QUERY_KEYS = [
  getImportsGetUserImportSourcesQueryKey(),
  getGetUserImportSourcesQueryKey(),
] as const;

export const IMPORT_OPERATIONS_QUERY_KEYS = [
  getListImportOperationsQueryKey(),
] as const;

export const ACCOUNTS_QUERY_KEYS = [
  getListAccountsQueryKey(),
] as const;

// Utility function to invalidate only app data queries
export const invalidateAppDataQueries = () => {
  return invalidateQueries(APP_DATA_QUERY_KEYS);
};

export const invalidateTradeAndImportDataQueries = () => {
  return invalidateQueries([
    ...APP_DATA_QUERY_KEYS,
    ...IMPORT_SOURCES_QUERY_KEYS,
    ...IMPORT_OPERATIONS_QUERY_KEYS,
    ...ACCOUNTS_QUERY_KEYS,
  ]);
};

export const invalidateQueries = (
  keys: ReadonlyArray<ReadonlyArray<string>>,
) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      return keys.some((key) => {
        // Check if the query key starts with the given key (prefix match)
        if (query.queryKey.length < key.length) return false;
        return key.every((keyPart, index) => query.queryKey[index] === keyPart);
      });
    },
    refetchType: "all",
  });
};

// Configure longer staleTime for app config and settings
queryClient.setQueryDefaults(getGetAppConfigQueryKey(), {
  staleTime: ONE_DAY_MS, // 1 day for app config
  retry: 0,
});
queryClient.setQueryDefaults(getAppConfigGetAppConfigQueryKey(), {
  staleTime: ONE_DAY_MS, // 1 day for app config
  retry: 0,
});

queryClient.setQueryDefaults(getGetSettingsQueryKey(), {
  staleTime: ONE_DAY_MS, // 1 day for settings
});
queryClient.setQueryDefaults(getSettingsReadSettingsQueryKey(), {
  staleTime: ONE_DAY_MS, // 1 day for settings
});

export const appDataLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getAppDataGetAppDataQueryOptions();
  queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

export const blockingAppDataLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getAppDataGetAppDataQueryOptions();
  await queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

export const blockingAppDataV2Loader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getGetAppDataWithParamsQueryOptions();
  await queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

// Loader for pages that need attributes (trades, analysis)
export const blockingAppDataV2WithAttributesLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getGetAppDataWithParamsQueryOptions({ include_attributes: true });
  await queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

// Smart loader that checks if tag columns need attributes
export const blockingAppDataV2SmartLoader = async () => {
  await getAuth().authStateReady();
  const needsAttributes = hasVisibleTagColumns();
  const queryOptions = getGetAppDataWithParamsQueryOptions({ include_attributes: needsAttributes });
  await queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

// Non-cancellable query options for app_config to prevent CancelledError
// on refresh when React's reconciliation causes navigation to be re-triggered
const getNonCancellableAppConfigQueryOptions = () => ({
  queryKey: getGetAppConfigQueryKey(),
  queryFn: () => getAppConfig(),
});

export const appConfigLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getNonCancellableAppConfigQueryOptions();
  queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

export const blockingAppConfigLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getNonCancellableAppConfigQueryOptions();
  await queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

// Helper to check feature flag from config response
const isFeatureFlagEnabled = (
  config: Awaited<ReturnType<typeof getAppConfig>>["data"],
  flagName: string
): boolean => {
  const flags = config.feature_flags ?? [];
  const flag = flags.find((f) => f.name === flagName);
  return flag?.enabled ?? false;
};

// Combined loader that loads app_config first, then app_data based on feature flag
// This ensures we use the correct API (v1 vs v2) based on user's feature flags
export const blockingAppConfigAndDataLoader = async () => {
  await getAuth().authStateReady();

  // First, load app_config to get feature flags
  const configQueryOptions = getNonCancellableAppConfigQueryOptions();
  const configResponse = await queryClient.ensureQueryData(configQueryOptions);

  // Check feature flag to determine which API to use
  const useV2Api = isFeatureFlagEnabled(configResponse.data, "use_v2_imports");

  // Load app_data with the appropriate API
  if (useV2Api) {
    // Use v2 API - check if tag columns need attributes
    const needsAttributes = hasVisibleTagColumns();
    const dataQueryOptions = getGetAppDataWithParamsQueryOptions({ include_attributes: needsAttributes });
    await queryClient.ensureQueryData(dataQueryOptions);
  } else {
    // Use v1 API
    const dataQueryOptions = getAppDataGetAppDataQueryOptions();
    await queryClient.ensureQueryData(dataQueryOptions);
  }

  return { configQueryOptions, useV2Api };
};

export const blockingInsightsLoader = async () => {
  await getAuth().authStateReady();
  const queryOptions = getInsightsGetInsightsQueryOptions();
  queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

export const blockingImportSourcesLoader = async () => {
  await getAuth().authStateReady();
  // Use accounts query which includes embedded import_sources
  const queryOptions = getListAccountsQueryOptions();
  queryClient.ensureQueryData(queryOptions);
  return queryOptions;
};

